import store, { AppState, setProductModel } from '../../state';
import ManifestTimelineHelper from '../../services/ManifestTimelineHelper';
import { ModelComparisonChartService, ModelMetaInfo } from '../../services/ModelComparisonChartService';

export class ModelComparisonDrawer {
  private static instance: ModelComparisonDrawer | null = null;
  private container: HTMLElement;
  private bodyContainer: HTMLElement;
  private ribbonContainer: HTMLElement;
  private toolbarContainer: HTMLElement;
  private titleEl: HTMLElement;
  private locationEl: HTMLElement;
  private isOpen: boolean = false;
  private modelsMeta: ModelMetaInfo[] = [];

  public static getInstance(): ModelComparisonDrawer {
    if (!this.instance) {
      this.instance = new ModelComparisonDrawer();
    }
    return this.instance;
  }

  constructor() {
    this.container = document.createElement('div');
    this.container.id = 'model-comparison-drawer';
    this.container.style.display = 'none';

    this.container.innerHTML = `
      <div class="model-comparison-header">
        <div class="model-comparison-title-wrap">
          <span class="model-comparison-icon">⚖️</span>
          <h3 class="model-comparison-title" id="mc-title">Model Comparison</h3>
          <span class="model-comparison-poi" id="mc-location">📍 Selected Location</span>
        </div>
        <div class="model-comparison-header-actions">
          <button type="button" class="mc-btn mc-expand-btn" id="mc-expand-btn" title="Toggle Expanded Height">
            <span id="mc-expand-icon">⤢</span> <span id="mc-expand-label">Expand</span>
          </button>
          <button type="button" class="mc-btn mc-close-btn" id="mc-close-btn" title="Close Model Comparison (Esc)">
            ✕ Close
          </button>
        </div>
      </div>
      <div class="model-comparison-controls" id="mc-toolbar"></div>
      <div class="model-comparison-body" id="mc-body"></div>
      <div class="model-comparison-ephemeris" id="mc-ephemeris"></div>
    `;

    document.body.appendChild(this.container);

    this.titleEl = this.container.querySelector('#mc-title') as HTMLElement;
    this.locationEl = this.container.querySelector('#mc-location') as HTMLElement;
    this.toolbarContainer = this.container.querySelector('#mc-toolbar') as HTMLElement;
    this.bodyContainer = this.container.querySelector('#mc-body') as HTMLElement;
    this.ribbonContainer = this.container.querySelector('#mc-ephemeris') as HTMLElement;

    // Close button
    const closeBtn = this.container.querySelector('#mc-close-btn') as HTMLElement;
    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.close());
    }

    // Expand/Minimize button
    const expandBtn = this.container.querySelector('#mc-expand-btn') as HTMLElement;
    if (expandBtn) {
      expandBtn.addEventListener('click', () => {
        const state = store.getState();
        store.update({ modelComparisonExpanded: !state.modelComparisonExpanded });
        setTimeout(() => {
          ModelComparisonChartService.resizeAll();
        }, 260);
      });
    }

    // Keyboard ESC listener
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Window resize listener
    window.addEventListener('resize', () => {
      if (this.isOpen) {
        ModelComparisonChartService.resizeAll();
      }
    });

    // Subscribe to state updates
    store.subscribe((state) => {
      if (state.showModelComparisonDrawer && !this.isOpen) {
        this.open(state.modelComparisonProduct);
      } else if (!state.showModelComparisonDrawer && this.isOpen) {
        this.close(false);
      } else if (this.isOpen) {
        this.updateFromState(state);
      }
    });
  }

  public open(productKey?: string, poi?: any): void {
    const state = store.getState();
    const effectiveProduct = productKey || state.modelComparisonProduct || state.primaryProduct || 'hrtemp';
    const effectivePoi = poi || state.selectedPoi;

    if (!effectivePoi) {
      console.warn('[ModelComparisonDrawer] Cannot open without active POI');
      return;
    }

    this.isOpen = true;
    this.container.style.display = 'flex';

    const needsStateUpdate = !state.showModelComparisonDrawer || state.modelComparisonProduct !== effectiveProduct;
    if (needsStateUpdate) {
      store.update({
        showModelComparisonDrawer: true,
        modelComparisonProduct: effectiveProduct,
        selectedPoi: effectivePoi
      });
    } else {
      this.updateFromState(store.getState());
    }
  }

  public close(updateState: boolean = true): void {
    this.isOpen = false;
    this.container.style.display = 'none';
    ModelComparisonChartService.cancelPending();

    if (updateState && store.getState().showModelComparisonDrawer) {
      store.update({ showModelComparisonDrawer: false });
    }
  }

  public onTimeStepChange(currentTimeStep: Date | null): void {
    if (this.isOpen) {
      ModelComparisonChartService.updateCursor(currentTimeStep);
    }
  }

  private updateFromState(state: AppState): void {
    if (!this.isOpen) return;

    // Apply multi-chart and expanded height classes
    const isMultiChart = state.modelComparisonMode === 'elevation' || state.modelComparisonMode === 'meteogram';
    const isExpanded = !!state.modelComparisonExpanded;
    this.container.classList.toggle('multi-chart', isMultiChart);
    this.container.classList.toggle('expanded', isExpanded);

    const expandIcon = this.container.querySelector('#mc-expand-icon');
    const expandLabel = this.container.querySelector('#mc-expand-label');
    if (expandIcon && expandLabel) {
      expandIcon.textContent = isExpanded ? '⤡' : '⤢';
      expandLabel.textContent = isExpanded ? 'Minimize' : 'Expand';
    }

    // Update Header
    const poi = state.selectedPoi;
    const isImperial = state.units === 'imperial';
    if (poi) {
      const elevStr = isImperial ? `${Math.round(poi.elev)} ft` : `${Math.round(poi.elev * 0.3048)} m`;
      const name = (poi as any).name || (poi as any).title || `${poi.lat.toFixed(3)}°, ${poi.lng.toFixed(3)}°`;
      this.locationEl.textContent = `📍 ${name} (~${elevStr})`;
    }

    const titles: Record<string, string> = {
      hrtemp: 'Temperature Forecast',
      sunrise: 'Sunrise & Sunset Quality',
      goldenhour: 'Golden Hour Quality',
      alpenglow: 'Alpenglow Quality',
      clouds: 'Cloud Cover',
      winds: 'Wind Speed',
      precip: 'Precipitation (Rate & Probability)',
      precip_daily: 'Daily Rain & Snow Accumulation',
      relative_humidity: 'Relative Humidity',
      freezing_level: 'Freezing Level',
      optics_dew_risk: 'Optics Dew Risk',
      darkskies: 'Dark Skies Quality',
      sun_intensity: 'Sun Intensity',
      lightning: 'Thunderstorm Probability',
      windchill: 'Wind Chill',
      smoke: 'Surface Smoke',
      snow_accum: 'Snowfall Accumulation (Past 3 Days)',
      snow_depth: 'Snowpack Depth & Extent'
    };
    const prodTitle = titles[state.modelComparisonProduct] || state.modelComparisonProduct.toUpperCase();
    this.titleEl.textContent = `Model Comparison: ${prodTitle}`;

    // Get models for product
    const queryProduct = (state.modelComparisonProduct === 'precip_daily') ? 'precip' : state.modelComparisonProduct;
    const availableModels = ManifestTimelineHelper.getAvailableModelsForProduct(state.manifest, queryProduct) || [];
    const modelsToRender = availableModels.length > 0 ? availableModels : [{ id: 'blend', label: 'SkySkout Weighted' }];

    // Render Toolbar (modes, horizons, model filter pills)
    this.renderToolbar(state, modelsToRender);

    // Render Charts
    ModelComparisonChartService.renderComparison(
      this.bodyContainer,
      this.ribbonContainer,
      state,
      modelsToRender
    ).then((meta) => {
      this.modelsMeta = meta;
      this.renderModelPills(state, modelsToRender);
      ModelComparisonChartService.updateCursor(state.currentTimeStep);
    }).catch((err) => {
      console.error('[ModelComparisonDrawer] Render failed:', err);
    });
  }

  private renderToolbar(state: AppState, modelsList: { id: string; label: string }[]): void {
    const supportsElevation = state.modelComparisonProduct === 'hrtemp' || state.modelComparisonProduct === 'winds';
    const isDailyProduct = ['sunrise', 'goldenhour', 'alpenglow', 'precip_daily', 'snow_accum'].includes(state.modelComparisonProduct);
    const activeMode = state.modelComparisonMode || 'single_product';
    const activeHorizon = state.modelComparisonHorizon || 3;
    const activeScale = state.modelComparisonScale || 'auto';

    const PRODUCT_OPTIONS = [
      { key: 'hrtemp', label: 'Temperature Forecast' },
      { key: 'sunrise', label: 'Sunrise & Sunset Quality' },
      { key: 'goldenhour', label: 'Golden Hour Quality' },
      { key: 'alpenglow', label: 'Alpenglow Quality' },
      { key: 'clouds', label: 'Cloud Cover' },
      { key: 'winds', label: 'Wind Speed' },
      { key: 'precip', label: 'Precipitation (Rate & Probability)' },
      { key: 'precip_daily', label: 'Daily Rain & Snow Accumulation' },
      { key: 'snow_accum', label: 'Snowfall Accumulation (Past 3 Days)' },
      { key: 'snow_depth', label: 'Snowpack Base Depth & Extent' },
      { key: 'relative_humidity', label: 'Relative Humidity' },
      { key: 'freezing_level', label: 'Freezing Level' },
      { key: 'optics_dew_risk', label: 'Optics Dew Risk' },
      { key: 'darkskies', label: 'Dark Skies Quality' },
      { key: 'sun_intensity', label: 'Sun Intensity' },
      { key: 'lightning', label: 'Thunderstorm Probability' },
      { key: 'windchill', label: 'Wind Chill' },
      { key: 'smoke', label: 'Surface Smoke' }
    ];

    this.toolbarContainer.innerHTML = `
      <div class="mc-toolbar-left">
        <select class="mc-product-select" id="mc-product-select" title="Switch Comparison Product">
          ${PRODUCT_OPTIONS.map(p => `
            <option value="${p.key}" ${state.modelComparisonProduct === p.key ? 'selected' : ''}>
              ${p.label}
            </option>
          `).join('')}
        </select>

        ${isDailyProduct ? '' : `
          <div class="mc-mode-group">
            <button type="button" class="mc-mode-btn ${activeMode === 'single_product' ? 'active' : ''}" data-mode="single_product">
              Single Forecast
            </button>
            ${supportsElevation ? `
              <button type="button" class="mc-mode-btn ${activeMode === 'elevation' ? 'active' : ''}" data-mode="elevation" title="Compare Peak, POI, and Valley ${state.modelComparisonProduct === 'winds' ? 'wind speeds' : 'temperatures'}">
                🏔️ Elevation Breakdown
              </button>
            ` : ''}
            <button type="button" class="mc-mode-btn ${activeMode === 'meteogram' ? 'active' : ''}" data-mode="meteogram" title="All-in-one multi-metric forecast">
              📊 All-in-One Meteogram
            </button>
          </div>
        `}

        <div class="mc-horizon-group">
          <span class="mc-group-label">Horizon:</span>
          ${[1, 3, 5, 10].map(h => `
            <button type="button" class="mc-horizon-btn ${activeHorizon === h ? 'active' : ''}" data-horizon="${h}">
              ${h === 10 ? 'MAX' : `${h}D`}
            </button>
          `).join('')}
        </div>

        <div class="mc-scale-group">
          <span class="mc-group-label">Y-Scale:</span>
          <button type="button" class="mc-scale-btn ${activeScale === 'auto' ? 'active' : ''}" data-scale="auto" title="Auto-scale Y-axis to accentuate model divergence">
            Auto
          </button>
          <button type="button" class="mc-scale-btn ${activeScale === 'fixed' ? 'active' : ''}" data-scale="fixed" title="Fixed standardized Y-axis scale">
            Fixed
          </button>
        </div>

        <div class="mc-consensus-group">
          <button type="button" class="mc-consensus-btn ${state.modelComparisonConsensus ? 'active' : ''}" id="mc-consensus-toggle" title="Toggle Consensus Ensemble Average line (averages all enabled models and fades individual traces)">
            ✨ Consensus Blend
          </button>
        </div>
      </div>
      <div class="mc-toolbar-models" id="mc-model-pills"></div>
    `;

    // Bind Product dropdown
    const prodSelect = this.toolbarContainer.querySelector('#mc-product-select') as HTMLSelectElement;
    if (prodSelect) {
      prodSelect.addEventListener('change', () => {
        const prod = prodSelect.value;
        const mapProd = (prod === 'precip_daily') ? 'precip' : prod;
        store.update({
          modelComparisonProduct: prod,
          primaryProduct: mapProd as any
        });
      });
    }

    // Bind Mode buttons
    const modeBtns = this.toolbarContainer.querySelectorAll('.mc-mode-btn');
    modeBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const mode = btn.getAttribute('data-mode') as 'single_product' | 'elevation' | 'meteogram';
        store.update({ modelComparisonMode: mode });
      });
    });

    // Bind Horizon buttons
    const horizonBtns = this.toolbarContainer.querySelectorAll('.mc-horizon-btn');
    horizonBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const horizon = Number(btn.getAttribute('data-horizon')) as 1 | 3 | 5 | 10;
        store.update({ modelComparisonHorizon: horizon });
      });
    });

    // Bind Scale buttons
    const scaleBtns = this.toolbarContainer.querySelectorAll('.mc-scale-btn');
    scaleBtns.forEach(btn => {
      btn.addEventListener('click', () => {
        const scale = btn.getAttribute('data-scale') as 'auto' | 'fixed';
        if (typeof window !== 'undefined') {
          localStorage.setItem('skyskout_comparison_scale', scale);
        }
        store.update({ modelComparisonScale: scale });
      });
    });

    // Bind Consensus Blend button
    const consensusBtn = this.toolbarContainer.querySelector('#mc-consensus-toggle');
    if (consensusBtn) {
      consensusBtn.addEventListener('click', () => {
        const nextVal = !store.getState().modelComparisonConsensus;
        if (typeof window !== 'undefined') {
          try {
            localStorage.setItem('skyskout_comparison_consensus', String(nextVal));
          } catch (_) {}
        }
        store.update({ modelComparisonConsensus: nextVal });
      });
    }

    this.renderModelPills(state, modelsList);
  }

  private renderModelPills(state: AppState, modelsList: { id: string; label: string }[]): void {
    const pillsContainer = this.toolbarContainer.querySelector('#mc-model-pills');
    if (!pillsContainer) return;

    const disabledSet = new Set(state.modelComparisonDisabledModels || []);
    const horizonDays = state.modelComparisonHorizon || 3;
    const requestedHours = horizonDays * 24;
    const mapProd = (state.modelComparisonProduct === 'precip_daily') ? 'precip' : state.modelComparisonProduct;
    const activeMapModel = (state.selectedModels[mapProd] || 'blend').toLowerCase();

    pillsContainer.innerHTML = modelsList.map(m => {
      const meta = this.modelsMeta.find(meta => meta.id.toLowerCase() === m.id.toLowerCase());
      const maxH = meta ? meta.maxHours : ModelComparisonChartService.getModelMaxHours(m.id);
      const isUnavail = maxH < 12; // Model has no usable data in this run
      const isPartial = maxH < requestedHours && !isUnavail;
      const isDisabled = disabledSet.has(m.id.toLowerCase());
      const isMapActive = activeMapModel === m.id.toLowerCase();
      const color = ModelComparisonChartService.getModelColor(m.id);

      let badge = `${maxH}h`;
      if (maxH >= 240) badge = '10d';
      else if (maxH >= 120) badge = `${Math.round(maxH / 24)}d`;
      if (isMapActive) badge = `★ ${badge}`;

      let pillClass = 'mc-model-pill';
      if (isUnavail) pillClass += ' unavail';
      else if (isDisabled) pillClass += ' disabled';
      else if (isPartial) pillClass += ' active partial';
      else pillClass += ' active';
      if (isMapActive) pillClass += ' map-active';

      const mapStatus = isMapActive ? ' (Active on Map)' : ' (Click to render on Map)';
      const tooltip = isUnavail
        ? `${m.label} (Unavailable in this run)`
        : isPartial
          ? `${m.label} (Ends at ${maxH}h)${mapStatus}`
          : `${m.label} (${maxH}h horizon)${mapStatus}`;

      return `
        <button type="button" class="${pillClass}" data-model="${m.id}" title="${tooltip}">
          <span class="mc-pill-dot" style="background-color: ${color};"></span>
          <span class="mc-pill-label">${m.id.toUpperCase()}</span>
          <span class="mc-pill-badge">${isUnavail ? 'Unavail' : badge}</span>
        </button>
      `;
    }).join('');

    // Bind pill click toggles and map model sync
    const pills = pillsContainer.querySelectorAll('.mc-model-pill:not(.unavail)');
    pills.forEach(pill => {
      pill.addEventListener('click', () => {
        const mId = pill.getAttribute('data-model');
        if (!mId) return;

        const currentDisabled = new Set(state.modelComparisonDisabledModels || []);
        const currentActiveMapModel = (state.selectedModels[mapProd] || 'blend').toLowerCase();

        if (currentDisabled.has(mId.toLowerCase())) {
          currentDisabled.delete(mId.toLowerCase());
          store.update({
            modelComparisonDisabledModels: Array.from(currentDisabled)
          });
          setProductModel(mapProd, mId.toLowerCase(), true);
        } else if (currentActiveMapModel !== mId.toLowerCase()) {
          // Switch map rendering to match this model
          setProductModel(mapProd, mId.toLowerCase(), true);
        } else {
          // If already active on map, toggle disabled on chart
          currentDisabled.add(mId.toLowerCase());
          store.update({ modelComparisonDisabledModels: Array.from(currentDisabled) });
        }
      });
    });
  }
}

export default ModelComparisonDrawer;
